import { useEffect, useState } from 'react';
import type { PlanDefinition, Contract, ContractAmendment } from '@hospasense/contracts';
import { useAuth } from '../lib/auth.js';
import {
  fetchPlans,
  fetchContracts,
  createContract,
  fetchAmendments,
  addAmendment,
  terminateContract,
  fetchOrganisationsList,
} from '../lib/queries-commercial.js';
import { CreateContractModal, AddAmendmentModal } from './commercial-modals.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  PageHeader,
  Badge,
  StatusPill,
  Table,
  TableContainer,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingState,
  ErrorState,
  PermissionDeniedState,
  Modal,
  Textarea,
  useToast,
} from '../components/ui/index.js';

export function CommercialPage() {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const [terminateTarget, setTerminateTarget] = useState<Contract | null>(null);
  const [terminateReason, setTerminateReason] = useState('');
  const allowed = hasPermission('contract.read');
  const [plans, setPlans] = useState<PlanDefinition[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [organisations, setOrganisations] = useState<Array<{ id: string; displayName: string }>>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [amendments, setAmendments] = useState<ContractAmendment[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAmendModal, setShowAmendModal] = useState(false);

  // Form states
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedPlanKey, setSelectedPlanKey] = useState<
    'starter_clinic' | 'growth_hospital' | 'enterprise_multispecialty' | 'academic_medical_center'
  >('growth_hospital');
  const [acvInr, setAcvInr] = useState(1200000);
  const [gstin, setGstin] = useState('29ABCDE1234F1Z5');
  const [stateCode, setStateCode] = useState('KA');
  const [amendSummary, setAmendSummary] = useState('');
  const [amendDelta, setAmendDelta] = useState(0);

  const loadData = async () => {
    try {
      setLoading(true);
      const [plansData, contractsData, orgsData] = await Promise.all([
        fetchPlans(),
        fetchContracts(),
        fetchOrganisationsList(),
      ]);
      setPlans(plansData);
      setContracts(contractsData.items);
      setOrganisations(orgsData);
      if (orgsData.length > 0) {
        setSelectedOrgId(orgsData[0]!.id);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allowed) {
      void loadData();
    }
  }, [allowed]);

  if (!allowed) return <PermissionDeniedState what="Commercial Contracts" />;

  const handleSelectContract = async (contract: Contract) => {
    setSelectedContract(contract);
    try {
      const amds = await fetchAmendments(contract.id);
      setAmendments(amds);
    } catch (err) {
      console.error('Failed to load amendments', err);
    }
  };

  const handleCreateContract = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const plan = plans.find((p) => p.key === selectedPlanKey);
      await createContract({
        organisationId: selectedOrgId,
        planKey: selectedPlanKey,
        startDate: new Date().toISOString(),
        endDate: new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString(),
        billingInterval: 'annual',
        annualContractValueInr: acvInr,
        discountPercent: 0,
        jurisdictionStateCode: stateCode,
        gstin,
        autoRenew: true,
        renewalNoticeDays: 30,
        gracePeriodDays: 15,
        includedModules: plan?.defaultModules ?? ['clinical_core', 'billing_invoicing'],
        capacityLimits: [
          {
            metric: 'licensed_beds',
            limitValue: 100,
            warningThresholdPercent: 85,
            overagePermitted: true,
            overageUnitRateInr: 500,
          },
        ],
      });
      setShowCreateModal(false);
      await loadData();
    } catch (err) {
      toast.error(`Contract creation failed: ${(err as Error).message}`);
    }
  };

  const handleAddAmendment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContract) return;
    try {
      await addAmendment(selectedContract.id, {
        amendmentType: 'capacity_expansion',
        effectiveDate: new Date().toISOString(),
        summary: amendSummary,
        changes: { licensed_beds: 150 },
        financialDeltaInr: amendDelta,
      });
      setShowAmendModal(false);
      setAmendSummary('');
      setAmendDelta(0);
      await handleSelectContract(selectedContract);
      await loadData();
    } catch (err) {
      toast.error(`Amendment failed: ${(err as Error).message}`);
    }
  };

  /**
   * Termination is a high-risk action: the reason is captured from the
   * operator and written to the audit trail verbatim. It is never
   * fabricated — a hardcoded reason would make every audit row claim the
   * same cause regardless of what actually happened.
   */
  const handleTerminate = async () => {
    const contract = terminateTarget;
    if (!contract) return;
    try {
      await terminateContract(contract.id, terminateReason.trim(), contract.version);
      await loadData();
      if (selectedContract?.id === contract.id) {
        setSelectedContract(null);
      }
      toast.success(`Contract ${contract.contractNumber} terminated`);
      setTerminateTarget(null);
      setTerminateReason('');
    } catch (err) {
      toast.error(`Termination failed: ${(err as Error).message}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="commercial-page">
      <PageHeader
        title="Commercial & Contracts"
        subtitle="System of record for commercial agreements, plans, and contract amendments"
        breadcrumbs={
          <Breadcrumbs items={[{ label: 'Control', to: '/' }, { label: 'Commercial' }]} />
        }
        actions={
          hasPermission('contract.manage') ? (
            <Button
              variant="primary"
              onClick={() => setShowCreateModal(true)}
              data-testid="create-contract-button"
            >
              + New Contract
            </Button>
          ) : undefined
        }
      />

      {error && <ErrorState description={error} onRetry={loadData} />}

      {/* Plans Catalog */}
      <div className="space-y-3">
        <h2 className="text-base font-semibold text-foreground">Standard Plans</h2>
        {!loading && plans.length === 0 && (
          <Card data-testid="plans-empty">
            <CardBody className="text-sm text-muted-foreground">
              No plans have been priced yet. Nothing shown on this page is an estimate — plans and
              contracts appear here only once they have actually been agreed.
            </CardBody>
          </Card>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {plans.map((p) => (
            <Card key={p.key} data-testid={`plan-card-${p.key}`}>
              <CardHeader
                title={p.name}
                actions={
                  <Badge tone={p.isCustom ? 'info' : 'neutral'}>
                    {p.isCustom ? 'Custom' : 'Standard'}
                  </Badge>
                }
              />
              <CardBody className="space-y-2 text-xs">
                <p className="text-muted-foreground">{p.description}</p>
                <div className="text-sm font-semibold text-foreground">
                  ₹{(p.basePriceAnnualInr / 100000).toFixed(1)}L / year
                </div>
                <div className="text-muted-foreground">
                  {p.defaultModules.length} Modules Included
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      </div>

      {/* Contracts Table */}
      <div className="space-y-3">
        <h2 className="text-base font-semibold text-foreground">Active Contracts</h2>
        <TableContainer>
          <Table>
            <THead>
              <TR>
                <TH>Contract #</TH>
                <TH>Organisation</TH>
                <TH>Plan</TH>
                <TH>Annual Value</TH>
                <TH>Status</TH>
                <TH>Actions</TH>
              </TR>
            </THead>
            <TBody>
              {loading ? (
                <TR>
                  <TD colSpan={6} className="text-center py-6">
                    <LoadingState label="Loading contracts…" />
                  </TD>
                </TR>
              ) : contracts.length === 0 ? (
                <TR>
                  <TD colSpan={6} className="text-center text-muted-foreground py-6">
                    No contracts yet — no hospital has signed one.
                  </TD>
                </TR>
              ) : (
                contracts.map((c) => (
                  <TR
                    key={c.id}
                    onClick={() => handleSelectContract(c)}
                    className="cursor-pointer hover:bg-surface-raised transition-colors"
                  >
                    <TD className="font-medium text-foreground">{c.contractNumber}</TD>
                    <TD className="font-medium text-foreground">
                      {c.organisationName ?? c.organisationId}
                    </TD>
                    <TD>{c.planName}</TD>
                    <TD>₹{(c.annualContractValueInr / 100000).toFixed(2)}L</TD>
                    <TD>
                      <StatusPill
                        tone={
                          c.status === 'active'
                            ? 'success'
                            : c.status === 'terminated'
                              ? 'error'
                              : 'warning'
                        }
                        label={c.status}
                      />
                    </TD>
                    <TD onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSelectContract(c)}
                          data-testid={`view-contract-${c.id}`}
                        >
                          Details
                        </Button>
                        {c.status === 'active' && hasPermission('contract.terminate') && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => setTerminateTarget(c)}
                            data-testid={`terminate-contract-${c.id}`}
                          >
                            Terminate
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        </TableContainer>
      </div>

      {/* Contract Detail Drawer */}
      {selectedContract && (
        <Card data-testid="contract-detail-drawer" className="space-y-4">
          <CardHeader
            title={`Contract Details: ${selectedContract.contractNumber}`}
            actions={
              <Button variant="ghost" size="sm" onClick={() => setSelectedContract(null)}>
                Close
              </Button>
            }
          />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div>
                <span className="text-muted-foreground block">Organisation</span>
                <span className="font-medium text-foreground">
                  {selectedContract.organisationName}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground block">Plan</span>
                <span className="font-medium text-foreground">{selectedContract.planName}</span>
              </div>
              <div>
                <span className="text-muted-foreground block">GSTIN / State</span>
                <span className="font-medium text-foreground">
                  {selectedContract.gstin ?? 'N/A'} ({selectedContract.jurisdictionStateCode})
                </span>
              </div>
              <div>
                <span className="text-muted-foreground block">Annual Value</span>
                <span className="font-medium text-foreground">
                  ₹{selectedContract.annualContractValueInr.toLocaleString('en-IN')}
                </span>
              </div>
            </div>

            <div>
              <span className="text-xs text-muted-foreground block mb-1">Included Modules</span>
              <div className="flex flex-wrap gap-1.5">
                {selectedContract.includedModules.map((m) => (
                  <Badge key={m} tone="neutral">
                    {m}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Amendments */}
            <div className="space-y-2 pt-2 border-t border-border">
              <div className="flex justify-between items-center">
                <h4 className="text-xs font-semibold text-foreground">Contract Amendments</h4>
                {hasPermission('contract.manage') && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowAmendModal(true)}
                    data-testid="add-amendment-button"
                  >
                    + Add Amendment
                  </Button>
                )}
              </div>
              {amendments.length === 0 ? (
                <p className="text-xs text-muted-foreground">No amendments on record</p>
              ) : (
                <div className="space-y-1.5">
                  {amendments.map((a) => (
                    <div
                      key={a.id}
                      className="p-2 bg-surface-raised rounded text-xs flex justify-between items-center border border-border"
                    >
                      <div>
                        <span className="font-semibold text-foreground">{a.amendmentNumber}</span>:{' '}
                        {a.summary}
                      </div>
                      <div className="text-muted-foreground">
                        Delta: ₹{a.financialDeltaInr.toLocaleString('en-IN')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Modals */}
      <CreateContractModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateContract}
        organisations={organisations}
        plans={plans}
        selectedOrgId={selectedOrgId}
        setSelectedOrgId={setSelectedOrgId}
        selectedPlanKey={selectedPlanKey}
        setSelectedPlanKey={setSelectedPlanKey}
        acvInr={acvInr}
        setAcvInr={setAcvInr}
        stateCode={stateCode}
        setStateCode={setStateCode}
        gstin={gstin}
        setGstin={setGstin}
      />

      <AddAmendmentModal
        open={showAmendModal}
        onClose={() => setShowAmendModal(false)}
        onSubmit={handleAddAmendment}
        amendSummary={amendSummary}
        setAmendSummary={setAmendSummary}
        amendDelta={amendDelta}
        setAmendDelta={setAmendDelta}
      />
      <Modal
        open={terminateTarget !== null}
        onClose={() => {
          setTerminateTarget(null);
          setTerminateReason('');
        }}
        title={`Terminate contract ${terminateTarget?.contractNumber ?? ''}`}
        footer={
          <Button
            variant="danger"
            disabled={terminateReason.trim().length < 10}
            onClick={() => void handleTerminate()}
          >
            Terminate contract
          </Button>
        }
      >
        <div className="space-y-2">
          <p className="text-xs text-text-muted">
            Termination is irreversible and is recorded in the audit trail. The reason you enter is
            stored verbatim, so state the actual cause.
          </p>
          <Textarea
            label="Termination reason"
            required
            value={terminateReason}
            onChange={(e) => setTerminateReason(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
}
